import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";
import usePageTitle from "app/pageTitle";
import MDInput from "theme/components/MDInput";
import MDButton from "theme/components/MDButton";
import DataTable from "theme/Tables/DataTable";
import axios from "axios.js";
import { useAwaitDialog } from "app/components/status_dialog";

function LoyaltyPromotionsPage() {
  const pageTitle = "Loyalty Promotions";
  usePageTitle(pageTitle);
  const navigate = useNavigate();
  const statusDialog = useAwaitDialog();
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const [refreshPage, setRefreshPage] = useState(false);
  const [pageLoaded, setPageLoaded] = useState(false);
  const [promotions, setPromotions] = useState([]);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [promotionTitle, setPromotionTitle] = useState("");
  const [promotionDescription, setPromotionDescription] = useState("");

  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const showMessage = (message, messageSeverity = "error") => {
    setSeverity(messageSeverity);
    setErrMsg(message || "An unexpected error occurred.");
    setOpen(true);
  };

  const tableColumns = [
    { Header: "Promotion", accessor: "title", align: "left" },
    { Header: "Status", accessor: "status", align: "center" },
    { Header: "Created", accessor: "created", align: "left" },
    { Header: "Actions", accessor: "actions", align: "right" },
  ];

  const buildRows = (data) => {
    const rows = [];
    data.map((row) => {
      rows.push({
        title: (
          <MDBox display="flex" flexDirection="column">
            <MDTypography variant="button" fontWeight="medium">
              {row.title}
            </MDTypography>
            <MDTypography variant="caption" color="text">
              {row.description || "---"}
            </MDTypography>
          </MDBox>
        ),
        status: (
          <MDTypography
            variant="button"
            fontWeight="medium"
            color={row.status ? "success" : "text"}
          >
            {row.status ? "Active" : "Inactive"}
          </MDTypography>
        ),
        created: row.created || "---",
        actions: (
          <MDBox mt={1} variant="button" display="flex" gap={1}>
            <Tooltip title="Manage Promotion">
              <MDButton
                size="small"
                variant="outlined"
                color="dark"
                iconOnly
                onClick={() => {
                  navigate(`/tm/loyalty/promotions/${row.reference}`);
                }}
              >
                <Icon sx={{ cursor: "pointer" }} fontSize="small">
                  settings
                </Icon>
              </MDButton>
            </Tooltip>
          </MDBox>
        ),
      });
      return null;
    });
    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get("/portal/tm/loyalty/promotions.json")
        .then((result) => {
          setShowLoading(false);
          if (result.data.status === "success") {
            const rows = buildRows(result.data.data.promotions || []);
            setPromotions(rows);
            setPageLoaded(true);
          } else {
            showMessage(result.data.data.message);
          }
        })
        .catch(() => {
          setShowLoading(false);
          showMessage(
            `Unable to complete request. ${
              navigator.onLine ? "" : "Make sure you have an active internet connection"
            }`
          );
        });
    };
    fetchData();
  }, [refreshPage]);

  const resetPromotionForm = () => {
    setPromotionTitle("");
    setPromotionDescription("");
  };

  async function createPromotion() {
    if (!promotionTitle.trim()) {
      showMessage("Promotion title is required.");
      return;
    }
    setOpenCreateDialog(false);
    setShowLoading(true);
    const payload = {
      title: promotionTitle.trim(),
      description: promotionDescription,
    };
    await axios
      .post("/portal/tm/loyalty/promotions/create.json", payload)
      .then(async (result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          resetPromotionForm();
          await statusDialog.show(
            result.data.data.message || "Promotion created successfully.",
            "success",
            "Success!"
          );
          setRefreshPage((value) => !value);
        } else {
          showMessage(result.data.data.message);
        }
      })
      .catch(() => {
        setShowLoading(false);
        showMessage(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
      });
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog
        open={openCreateDialog}
        fullWidth
        maxWidth="sm"
        onClose={() => setOpenCreateDialog(false)}
      >
        <DialogTitle>
          <MDBox>Create Loyalty Promotion</MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox>
            <MDBox mt={1}>
              <MDInput
                name="title"
                margin="dense"
                label="Promotion Title"
                type="text"
                fullWidth
                variant="outlined"
                value={promotionTitle}
                onChange={(e) => setPromotionTitle(e.target.value)}
              />
            </MDBox>
            <MDBox mt={2}>
              <MDInput
                name="description"
                margin="dense"
                label="Description"
                type="text"
                fullWidth
                multiline
                rows={3}
                variant="outlined"
                value={promotionDescription}
                onChange={(e) => setPromotionDescription(e.target.value)}
              />
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenCreateDialog(false);
              resetPromotionForm();
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              createPromotion();
            }}
          >
            Create
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={1} mb={5}>
        {pageLoaded && (
          <MDBox>
            <MDBox>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox p={1}>
                  <MDBox align="center">
                    <MDBox>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon>card_giftcard</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontWeight="bold" fontSize={16}>
                        Loyalty Promotions
                      </MDTypography>
                      <MDTypography mt={1} variant="h6" fontWeight="light" fontSize={16}>
                        Create and manage loyalty promotion campaigns.
                        <br />
                        Configure products, rewards, groups, and territories.
                      </MDTypography>
                    </MDBox>
                    <MDBox mt={3} mb={3}>
                      <MDButton
                        variant="contained"
                        color="dark"
                        onClick={() => {
                          resetPromotionForm();
                          setOpenCreateDialog(true);
                        }}
                        startIcon={<Icon>add</Icon>}
                      >
                        Create Promotion
                      </MDButton>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
            <MDBox mt={5}>
              <Card sx={{ height: "100%", marginTop: "0px" }}>
                <MDBox>
                  <MDBox p={2}>
                    <MDBox display="flex" justifyContent="space-between" alignItems="center">
                      <MDBox>
                        <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                          Promotions
                        </MDTypography>
                      </MDBox>
                      <MDBox>
                        <MDButton
                          variant="contained"
                          color="dark"
                          iconOnly
                          onClick={() => {
                            resetPromotionForm();
                            setOpenCreateDialog(true);
                          }}
                        >
                          <Icon>add</Icon>
                        </MDButton>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                  <MDBox p={0}>
                    <DataTable
                      table={{ columns: tableColumns, rows: promotions }}
                      canSearch={false}
                      isSorted
                      entriesPerPage={false}
                      showTotalEntries
                      buttons
                      noEndBorder
                    />
                  </MDBox>
                </MDBox>
              </Card>
            </MDBox>
          </MDBox>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default LoyaltyPromotionsPage;
